/**
 * ContentPipelinePanel — the workspace's idea pipeline: compact stage counts
 * plus only the ideas awaiting the SMM's approval. Extracted from the old
 * Overview command center; owns its idea fetching and the CreateIdeaModal,
 * so "+ New Idea" lives in this panel's header.
 */

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckIcon,
  ChevronRightIcon,
  LightbulbIcon,
  PlusIcon,
} from "@/components/icons";
import { EntityCreateButton, ENTITY_CONFIG } from "@/core/entityConfig";
import { useTrailNavOptions } from "@/core/navigation/trail";
import {
  useCreateIdeaMutation,
  useIdeasQuery,
} from "@/domains/Ideas/hooks/useIdeas";
import { CreateIdeaModal } from "@/domains/Ideas/components/CreateIdeaModal";
import { IdeaStatusBadge } from "@/domains/Ideas/components/IdeaStatusBadge";
import { CreateIdeaRequest, Idea } from "@/domains/Ideas/types";
import { deriveStages } from "@/domains/Ideas/ideaStageState";
import {
  resolveObjectiveLabel,
  useObjectiveTypes,
} from "@/domains/Ideas/services/objectiveTypes";
import type { ObjectiveType } from "@/domains/Ideas/services/objectiveTypes";
import {
  deriveIdeaStageInput,
  derivePipelineFilters,
  filterPipelineIdeas,
  PipelineFilterId,
  summarizeActiveIdeas,
} from "../homeIdeaPipeline";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { PanelList } from "@/components/ui/PanelList";
import { PanelListItem } from "@/components/ui/PanelListItem";
import { PanelFilterTabs } from "@/components/ui/PanelFilterTabs";
import { useCareScore } from "../hooks/useCareScore";

const CompactStageRail: React.FC<{ idea: Idea }> = ({ idea }) => {
  const stages = useMemo(() => deriveStages(deriveIdeaStageInput(idea)), [idea]);
  return (
    <div className="flex items-center gap-1.5" aria-label="Pipeline stage">
      {stages.map((stage, index) => (
        <React.Fragment key={stage.id}>
          {index > 0 && <span className="w-3 h-px bg-gray-200" aria-hidden />}
          <span
            title={stage.detail ? `${stage.label}: ${stage.detail}` : stage.label}
            className={`flex items-center justify-center w-5 h-5 rounded-full ${stage.dotColor} ${
              stage.done ? "" : "opacity-70"
            }`}
          >
            {stage.done ? (
              <CheckIcon className="w-3 h-3 text-white" />
            ) : (
              <span className="text-[9px] font-bold text-white">{index + 1}</span>
            )}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

const IdeaRow: React.FC<{
  idea: Idea;
  objectiveTypes: ObjectiveType[];
  onOpen: () => void;
}> = ({ idea, objectiveTypes, onOpen }) => {
  const objectiveLabel = resolveObjectiveLabel(idea.objectiveType, objectiveTypes);
  return (
    <PanelListItem
      title={idea.title?.trim() || "Untitled idea"}
      meta={
        <>
          <IdeaStatusBadge status={idea.status} />
          {objectiveLabel && (
            <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full bg-gray-100 text-gray-600">
              {objectiveLabel}
            </span>
          )}
        </>
      }
      trailing={
        <div className="hidden sm:block">
          <CompactStageRail idea={idea} />
        </div>
      }
      onPress={onOpen}
    />
  );
};

interface ContentPipelinePanelProps {
  clientId: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export const ContentPipelinePanel: React.FC<ContentPipelinePanelProps> = ({
  clientId,
  collapsed,
  onToggleCollapse,
}) => {
  const navigate = useNavigate();
  const trailNavOptions = useTrailNavOptions(ENTITY_CONFIG.home.label);

  const { data: careData } = useCareScore({ clientId });
  const publishedThisWeek = careData?.details?.publishedThisWeek ?? 0;

  const { data: ideas = [], isLoading } = useIdeasQuery(
    clientId,
    undefined,
    true,
    false
  );
  const { data: objectiveTypes = [] } = useObjectiveTypes();
  const createMutation = useCreateIdeaMutation(clientId);
  const [isCreating, setIsCreating] = useState(false);
  const [statusFilter, setStatusFilter] = useState<PipelineFilterId>("ALL");

  const summary = useMemo(() => summarizeActiveIdeas(ideas), [ideas]);
  const filterOptions = useMemo(() => derivePipelineFilters(ideas), [ideas]);
  const visibleIdeas = useMemo(
    () => filterPipelineIdeas(ideas, statusFilter),
    [ideas, statusFilter]
  );

  const openIdea = (id: string) =>
    navigate(`/app/${clientId}/ideas/${id}`, trailNavOptions);
  const goToIdeas = () => navigate(`/app/${clientId}/ideas`);

  const handleCreate = async (data: CreateIdeaRequest) => {
    const idea = await createMutation.mutateAsync(data);
    setIsCreating(false);
    navigate(`/app/${clientId}/ideas/${idea.id}`, trailNavOptions);
  };

  return (
    <>
      <WorkspacePanel
        id="pipeline"
        title="Content pipeline"
        icon={<LightbulbIcon className="w-4 h-4" />}
        summary={`${summary.active} active · ${publishedThisWeek} published this week`}
        loading={isLoading}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        className={collapsed ? "" : "h-full"}
        bodyClassName="min-h-[48rem] max-h-[48rem]"
        subheader={
          <div className="flex items-center justify-between gap-3">
            <PanelFilterTabs
              options={filterOptions}
              activeId={statusFilter}
              onChange={(id) => setStatusFilter(id as PipelineFilterId)}
            />
            <PanelHeaderAction
              label="All ideas"
              icon={<ChevronRightIcon className="w-4 h-4" />}
              onClick={goToIdeas}
              variant="secondary"
            />
          </div>
        }
        actions={
          <PanelHeaderAction
            label={ENTITY_CONFIG.idea.createLabel}
            icon={<PlusIcon className="w-4 h-4" />}
            onClick={() => setIsCreating(true)}
          />
        }
      >
        {summary.active === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-8 px-6 rounded-xl border border-dashed border-gray-200 bg-gray-50/60">
            <span className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-50 text-amber-500">
              <LightbulbIcon className="w-6 h-6" />
            </span>
            <h4 className="mt-4 text-sm font-semibold text-gray-900">
              No active ideas
            </h4>
          </div>
        ) : (
          <PanelList
            items={visibleIdeas}
            keyFor={(i) => i.id}
            empty={
              <p className="text-sm text-gray-500 py-6 text-center">
                Nothing in this stage.
              </p>
            }
            renderItem={(idea) => (
              <IdeaRow
                idea={idea}
                objectiveTypes={objectiveTypes}
                onOpen={() => openIdea(idea.id)}
              />
            )}
          />
        )}
      </WorkspacePanel>

      <CreateIdeaModal
        isOpen={isCreating}
        onClose={() => setIsCreating(false)}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />
    </>
  );
};
